import React, { useCallback, useEffect } from 'react'
import { Link, useHistory, useParams } from 'react-router-dom'
import { Breadcrumb, Col, Row, Space, Spin } from 'antd'
import { isEmpty, isNil } from 'lodash'

import { UpdateDataText } from '../../../core/components/sections'

import { HistoryLink } from '../../../common/components/sections'
import { StyledPageSubTitle } from '../../../common/components/styled'
import { APP_ROUTES, EVENT_METADATA_TYPES } from '../../../common/constants'
import { showErrorNotification } from '../../../common/helpers'
import {
  useCuisineTypesQuery,
  useDocumentTitle,
  useLanguagesQuery,
} from '../../../common/hooks'

import { renderUserEvent } from '../../helpers'
import {
  useCreateUserMutation,
  useRolesQuery,
  useUpdateUserMutation,
  useUserDetailsQuery,
} from '../../hooks'
import { UserEditorForm } from '../forms'

const UserEditorPage = () => {
  const { id: userId } = useParams()
  const documentTitle = userId ? 'Edit user' : 'Create user'
  useDocumentTitle(documentTitle)
  const history = useHistory()

  const [
    fetchUserDetails,
    { data: user, isLoading: isLoadingUser, error: userError },
  ] = useUserDetailsQuery(true)

  const [createUser] = useCreateUserMutation()
  const [updateUser] = useUpdateUserMutation()

  const {
    options: roleOptions,
    isLoading: isLoadingRoles,
    error: roleError,
  } = useRolesQuery()

  const {
    options: languageOptions,
    isLoading: isLoadingLanguages,
    error: languageError,
  } = useLanguagesQuery()

  const {
    options: cuisineTypeOptions,
    isLoading: isLoadingCuisineTypes,
    error: cuisineTypesError,
  } = useCuisineTypesQuery()

  const handleSubmit = useCallback(
    userData => {
      if (userId) {
        updateUser(userId, userData)
      } else {
        createUser(userData)
      }
    },
    [createUser, updateUser, userId],
  )

  useEffect(() => {
    if (isNil(userId) && !isNil(user?.id)) {
      history.push(`${APP_ROUTES.OTHER.subMenuItems.USERS.link}/${user?.id}`)
    }
  }, [history, user?.id, userId])

  useEffect(() => {
    if (!isNil(userId)) {
      fetchUserDetails(userId)
    }
  }, [fetchUserDetails, userId])

  useEffect(() => {
    if (userError || roleError || languageError || cuisineTypesError) {
      showErrorNotification({
        title: 'User error',
        description:
          userError || roleError || languageError || cuisineTypesError,
      })
    }
  }, [cuisineTypesError, languageError, roleError, userError])

  if (
    (!isNil(userId) && isEmpty(user)) ||
    isLoadingUser ||
    isEmpty(languageOptions) ||
    isLoadingLanguages ||
    isEmpty(cuisineTypeOptions) ||
    isLoadingCuisineTypes ||
    isEmpty(roleOptions) ||
    isLoadingRoles
  ) {
    return <Spin />
  }

  return (
    <>
      <StyledPageSubTitle>
        <Row justify="space-between">
          <Col span={12}>
            <Breadcrumb>
              <Breadcrumb.Item>
                <Link to={APP_ROUTES.OTHER.subMenuItems.USERS.link}>
                  {APP_ROUTES.OTHER.subMenuItems.USERS.name}
                </Link>
              </Breadcrumb.Item>
              <Breadcrumb.Item>{user?.name}</Breadcrumb.Item>
            </Breadcrumb>
          </Col>

          {user && (
            <Space>
              <UpdateDataText
                updatedAt={user.updatedAt}
                updatedBy={user.updatedBy?.name}
              />

              <HistoryLink
                filterEventTypes
                eventMetadataTypes={[EVENT_METADATA_TYPES.USER]}
                renderEvent={renderUserEvent}
              />
            </Space>
          )}
        </Row>
      </StyledPageSubTitle>
      <UserEditorForm
        user={user}
        roleOptions={roleOptions}
        cuisineTypeOptions={cuisineTypeOptions}
        languageOptions={languageOptions}
        onSubmit={handleSubmit}
      />
    </>
  )
}

export default UserEditorPage
